'use client';

import { RefObject, useCallback, useEffect, useRef } from 'react';

interface UseSwipeToCloseOptions {
  enabled?: boolean;
  threshold?: number;
  velocityThreshold?: number;
  onSwipeStart?: () => void;
  onSwipeMove?: (displacement: number) => void;
  onSwipeEnd?: () => void;
  onClose: () => void;
}

interface SwipeState {
  isDragging: boolean;
  startY: number;
  startTime: number;
  currentY: number;
  displacement: number;
  pointerId: number | null;
  velocityHistory: Array<{ time: number; y: number }>;
}

/**
 * 바텀시트를 스와이프로 닫을 수 있게 해주는 커스텀 훅
 * Vaul 라이브러리의 구현을 참고하여 제작
 */
export function useSwipeToClose(ref: RefObject<HTMLElement | null>, options: UseSwipeToCloseOptions) {
  const {
    enabled = true,
    threshold = 100,
    velocityThreshold = 0.5,
    onSwipeStart,
    onSwipeMove,
    onSwipeEnd,
    onClose,
  } = options;

  const swipeStateRef = useRef<SwipeState>({
    isDragging: false,
    startY: 0,
    startTime: 0,
    currentY: 0,
    displacement: 0,
    pointerId: null,
    velocityHistory: [],
  });

  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      if (!enabled || !ref.current) return;

      if (swipeStateRef.current.pointerId !== null) return;

      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const target = event.target as HTMLElement;
      const isDragHandle = target.closest('[data-drag-handle]');
      if (ref.current.querySelector('[data-drag-handle]') && !isDragHandle) {
        return;
      }
      // https://github.com/GoogleChromeLabs/pointer-tracker/issues/4
      (target ?? ref.current).setPointerCapture(event.pointerId);

      const now = Date.now();
      swipeStateRef.current = {
        isDragging: true,
        startY: event.clientY,
        startTime: now,
        currentY: event.clientY,
        displacement: 0,
        pointerId: event.pointerId,
        velocityHistory: [{ time: now, y: event.clientY }],
      };

      onSwipeStart?.();
    },
    [enabled, ref, onSwipeStart],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!enabled || !swipeStateRef.current.isDragging) return;

      // 현재 추적 중인 포인터가 아니면 무시
      if (event.pointerId !== swipeStateRef.current.pointerId) return;

      const currentY = event.clientY;
      const displacement = Math.max(0, currentY - swipeStateRef.current.startY);
      const now = Date.now();

      const velocityHistory = swipeStateRef.current.velocityHistory.filter((entry) => now - entry.time < 100);
      velocityHistory.push({ time: now, y: currentY });

      swipeStateRef.current.currentY = currentY;
      swipeStateRef.current.displacement = displacement;
      swipeStateRef.current.velocityHistory = velocityHistory;

      if (displacement > 0) {
        event.preventDefault();
        onSwipeMove?.(displacement);
      }
    },
    [enabled, onSwipeMove],
  );

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      if (!enabled || !swipeStateRef.current.isDragging) return;

      if (event.pointerId !== swipeStateRef.current.pointerId) return;

      const { displacement, velocityHistory } = swipeStateRef.current;
      let velocity = 0;
      if (velocityHistory.length >= 2) {
        const recent = velocityHistory.slice(-2);
        const timeDiff = recent[1].time - recent[0].time;
        const yDiff = recent[1].y - recent[0].y;
        velocity = timeDiff > 0 ? yDiff / timeDiff : 0; // px/ms
      }
      swipeStateRef.current.isDragging = false;
      swipeStateRef.current.pointerId = null;
      swipeStateRef.current.velocityHistory = [];

      onSwipeEnd?.();

      const shouldClose =
        displacement >= threshold || // 충분한 거리 스와이프
        (displacement > 50 && velocity >= velocityThreshold); // 빠른 속도로 스와이프

      if (shouldClose) {
        onClose();
      }
    },
    [enabled, threshold, velocityThreshold, onSwipeEnd, onClose],
  );

  const handlePointerCancel = useCallback(
    (event: PointerEvent) => {
      if (!enabled || !swipeStateRef.current.isDragging) return;

      if (event.pointerId !== swipeStateRef.current.pointerId) return;
      swipeStateRef.current.isDragging = false;
      swipeStateRef.current.pointerId = null;
      swipeStateRef.current.velocityHistory = [];

      onSwipeEnd?.();
    },
    [enabled, onSwipeEnd],
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    element.addEventListener('pointerdown', handlePointerDown);
    element.addEventListener('pointermove', handlePointerMove);
    element.addEventListener('pointerup', handlePointerUp);
    element.addEventListener('pointercancel', handlePointerCancel);

    const originalTouchAction = element.style.touchAction;
    element.style.touchAction = 'pan-x pan-zoom-out';

    return () => {
      element.removeEventListener('pointerdown', handlePointerDown);
      element.removeEventListener('pointermove', handlePointerMove);
      element.removeEventListener('pointerup', handlePointerUp);
      element.removeEventListener('pointercancel', handlePointerCancel);

      element.style.touchAction = originalTouchAction;
    };
  }, [enabled, handlePointerDown, handlePointerMove, handlePointerUp, handlePointerCancel, ref]);

  return {
    isDragging: swipeStateRef.current.isDragging,
    displacement: swipeStateRef.current.displacement,
  };
}
